import { getCurrentToken } from '@qse/api'
import { AppRoutesList, FolderUploadProgressPayload, UserSuspendedPayload } from '@qse/constants'
import { useRealtime } from '@qse/realtime'
import { useBillingStatusStore, useUserStore } from '@qse/stores'
import { toast } from '@qse/ui'
import { forceLogout } from '@qse/utils'
import { useEffect, useRef } from 'react'

import { canConnectRealtime } from './realtime-eligibility'
import { useRouteContext } from './use-route-context'

// Extend the existing payload types for payment events
interface PaymentFailurePayload {
  tenantId: string
  subscriptionId: string
  failureReason: string
  gracePeriodStart: string
  gracePeriodEnd: string
  retryAttempts: number
  nextRetryDate?: string
}

interface PaymentResolvedPayload {
  tenantId: string
  subscriptionId: string
  resolvedAt: string
  newBillingStatus: 'active' | 'past_due' | 'cancelled'
}

interface GracePeriodExpiredPayload {
  tenantId: string
  subscriptionId: string
  expiredAt: string
  finalStatus: 'cancelled' | 'suspended'
}

/**
 * Custom real-time hook for dashboard app with drive-specific event handlers
 */
export function useDriveRealtime() {
  const routeContext = useRouteContext()
  const { user } = useUserStore()
  const {
    setBillingStatus,
    setGracePeriodStatus,
    addPaymentFailureNotification,
    setShowPaymentOverdueBanner,
  } = useBillingStatusStore()

  // Track folder uploads that have already shown success toast to prevent duplicates
  const completedFolderUploadsRef = useRef<Set<string>>(new Set())

  const realtime = useRealtime({
    getToken: getCurrentToken,
    shouldConnect: () => canConnectRealtime(user?.userRole),
    getRouteContext: () => routeContext,
    eventHandlers: {
      onUserSuspended: (payload: UserSuspendedPayload) => {
        console.log('🚫 Dashboard user suspended:', payload)
        // Force logout immediately for dashboard users
        forceLogout(true)
      },

      // Payment failure event handlers
      onPaymentFailure: (payload: PaymentFailurePayload) => {
        console.log('💳 Payment failure detected:', payload)

        // Update billing status to past_due
        setBillingStatus('past_due')

        // Set grace period status
        setGracePeriodStatus({
          isInGracePeriod: true,
          gracePeriodStart: payload.gracePeriodStart,
          gracePeriodEnd: payload.gracePeriodEnd,
          daysRemaining: Math.ceil(
            (new Date(payload.gracePeriodEnd).getTime() - new Date().getTime()) /
              (1000 * 60 * 60 * 24),
          ),
          paymentFailureReason: payload.failureReason,
          retryAttempts: payload.retryAttempts,
          nextRetryDate: payload.nextRetryDate,
        })

        // Add payment failure notification
        addPaymentFailureNotification({
          id: `payment-failure-${Date.now()}`,
          tenantId: payload.tenantId,
          subscriptionId: payload.subscriptionId,
          failureReason: payload.failureReason,
          failureDate: new Date().toISOString(),
          gracePeriodStart: payload.gracePeriodStart,
          gracePeriodEnd: payload.gracePeriodEnd,
          isActive: true,
          retryAttempts: payload.retryAttempts,
          nextRetryDate: payload.nextRetryDate,
        })

        // Show payment overdue banner
        setShowPaymentOverdueBanner(true)

        // Show notification to user
        toast.error(`Payment failed: ${payload.failureReason}`, {
          description: 'Your account is now in a grace period. Please update your payment method.',
          duration: 10000,
        })
      },

      onPaymentResolved: (payload: PaymentResolvedPayload) => {
        console.log('✅ Payment resolved:', payload)

        // Update billing status
        setBillingStatus(payload.newBillingStatus)

        // Clear grace period if payment is now active
        if (payload.newBillingStatus === 'active') {
          setGracePeriodStatus(null)
          setShowPaymentOverdueBanner(false)

          toast.success('Payment resolved successfully!', {
            description: 'Your subscription is now active.',
          })
        }
      },

      onGracePeriodExpired: (payload: GracePeriodExpiredPayload) => {
        console.log('⏰ Grace period expired:', payload)

        // Update billing status to cancelled
        setBillingStatus('cancelled')

        // Clear grace period
        setGracePeriodStatus(null)

        // Show critical notification
        toast.error('Grace period expired', {
          description:
            'Your account has been suspended due to payment issues. Please contact support immediately.',
          duration: 15000,
        })

        // Redirect to billing page
        setTimeout(() => {
          window.location.href = AppRoutesList.billing
        }, 2000)
      },

      onSubscriptionRetryAttempt: (payload: PaymentFailurePayload) => {
        console.log('🔄 Subscription retry attempt:', payload)

        // Update grace period status with new retry information
        setGracePeriodStatus({
          isInGracePeriod: true,
          gracePeriodStart: payload.gracePeriodStart,
          gracePeriodEnd: payload.gracePeriodEnd,
          daysRemaining: Math.ceil(
            (new Date(payload.gracePeriodEnd).getTime() - new Date().getTime()) /
              (1000 * 60 * 60 * 24),
          ),
          paymentFailureReason: payload.failureReason,
          retryAttempts: payload.retryAttempts,
          nextRetryDate: payload.nextRetryDate,
        })

        toast.info('Payment retry attempted', {
          description: `Retry attempt ${payload.retryAttempts} completed. Next retry: ${payload.nextRetryDate ? new Date(payload.nextRetryDate).toLocaleDateString() : 'TBD'}`,
        })
      },

      // Billing state change events
      onBillingPastDue: (_payload: any) => {
        setBillingStatus('past_due')
        setShowPaymentOverdueBanner(true)
      },

      onBillingUnpaid: (_payload: any) => {
        forceLogout(true)
      },

      onBillingCanceled: (_payload: any) => {
        forceLogout(true)
      },

      // Folder upload progress event handler
      onFolderUploadProgress: (payload: FolderUploadProgressPayload) => {
        console.log('📁 Folder upload progress:', payload)

        // Show progress notification for folder uploads
        const progress = payload.progress
        const uploadProgress = progress.upload
        const encryptionProgress = progress.encryption

        // IMPORTANT: Compare against payload.totalFiles (the actual total from folder scan)
        // NOT uploadProgress.total (which might represent just the current batch)
        // This ensures we only show success toast after ALL batches are complete
        const allFilesUploaded = uploadProgress.completed >= payload.totalFiles
        const allFilesEncrypted = encryptionProgress.completed >= payload.totalFiles

        // Show different notifications based on progress
        if (allFilesUploaded && allFilesEncrypted) {
          // Check if we've already shown success toast for this folder to prevent duplicates
          if (!completedFolderUploadsRef.current.has(payload.folderId)) {
            // Mark this folder as completed
            completedFolderUploadsRef.current.add(payload.folderId)
          }
        } else if (uploadProgress.failed > 0 || encryptionProgress.failed > 0) {
          // Some files failed - don't show toast messages for individual file failures during folder upload
          // The progress tracker will show the failure count in the UI
        } else if (uploadProgress.completed > 0 || encryptionProgress.completed > 0) {
          // Don't show toast messages for ongoing folder uploads
          // The progress tracker will show the progress in the UI
        }
      },
    },
  })

  // Log connection state changes for debugging
  useEffect(() => {
    const { isConnected, isConnecting, error } = realtime.connectionState

    if (isConnected) {
      console.log('✅ Dashboard WebSocket connected successfully')
    } else if (isConnecting) {
      console.log('🔄 Dashboard WebSocket connecting...')
    } else if (error) {
      console.warn('❌ Dashboard WebSocket error:', error)
    }
  }, [realtime.connectionState])

  return realtime
}
